import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Button, ButtonProps } from '@atlassiansox/wpl-utils';
import { buttonContainerStyle, containerStyle } from './add-product.styles';
import ProductModal from './add-product-modal';
import { ProductName } from '../../models';
import { useSelectedProductNames } from '../../selectors';
import { ANALYTICS_EVENTS, PRODUCT_NAMES } from '../../constants';
import { useGlobalStore } from '../../store';
import {
  WithAnalyticsEventsProps,
  withAnalyticsEvents
} from '@atlaskit/analytics-next';
import { useModuleProps } from '../../module-props';

const AddButtonBase = ({
  createAnalyticsEvent,
  onClick,
  ...rest
}: WithAnalyticsEventsProps & ButtonProps) => {
  const handleClick = useCallback(
    async (event: ReactMouseEvent) => {
      const analyticsEvent = createAnalyticsEvent?.({
        action: 'click',
        component: 'button',
        eventName: ANALYTICS_EVENTS.openProductSelection
      });
      analyticsEvent?.fire();

      if (onClick) {
        onClick(event);
      }
    },
    [onClick, createAnalyticsEvent]
  );

  return <Button {...rest} onClick={handleClick} />;
};

const AddButton = withAnalyticsEvents()(AddButtonBase);

const AddProduct = () => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { addProductLabel } = useModuleProps();

  const [selectedProducts] = useSelectedProductNames();
  const availableProducts = PRODUCT_NAMES.filter(
    (productName) => !selectedProducts.includes(productName)
  );

  const [_, { toggleProductSelection }] = useGlobalStore();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        isModalOpen
      ) {
        setIsModalOpen(!isModalOpen);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  return (
    <div css={containerStyle} data-testid="add-product">
      <div ref={modalRef} css={buttonContainerStyle}>
        <AddButton
          text={`+ ${addProductLabel}`}
          color="lightblue"
          onClick={() => setIsModalOpen(!isModalOpen)}
          disabled={availableProducts.length === 0}
        />
        {isModalOpen && (
          <ProductModal
            productList={availableProducts}
            onSelectProduct={(selectedProductName: ProductName) => {
              toggleProductSelection(selectedProductName, true);
              setIsModalOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AddProduct;
